import React, { ReactNode, useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import Tag from "antd/es/tag";

import DealLossReason from "@mapmycustomers/shared/types/entity/deals/DealLossReason";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import defaultFilterOption from "@mapmycustomers/shared/util/input/defaultFilterOption";
import { InlineSelectField } from "@mapmycustomers/ui";

import SelectField, { SelectFieldProps } from "@app/component/input/SelectField";
import styles from "@app/component/input/UserMultiSelectField/UserMultiSelectField.module.scss";
import { fetchDealLossReasons } from "@app/store/dealLossReasons/actions";
import { getDealLossReasons, isDealLossReasonsLoading } from "@app/store/dealLossReasons/selectors";
import { RootState } from "@app/store/rootReducer";
import defaultSelectOptionMapper from "@app/util/mappers/defaultSelectOptionMapper";

interface Props extends Pick<SelectFieldProps, "allowClear" | "disabled" | "placeholder" | "size"> {
  className?: string;
  inline?: boolean;
  label?: ReactNode;
  onChange: (lossReasons: DealLossReason["id"][]) => void;
  value?: DealLossReason["id"][];
}

const LossReasonSelect = ({
  className,
  fetchDealLossReasons,
  inline,
  label,
  loading,
  lossReasons,
  onChange,
  value,
  ...props
}: Props & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  useEffect(() => {
    fetchDealLossReasons();
  }, [fetchDealLossReasons]);

  const selectOptions = useMemo(() => lossReasons.map(defaultSelectOptionMapper), [lossReasons]);

  const SelectComponent = inline
    ? InlineSelectField<DealLossReason["id"][]>
    : SelectField<DealLossReason["id"][]>;

  const valueRender = useCallback(
    (lossReasonIds: DealLossReason["id"][] | null | undefined) => {
      if (!lossReasonIds?.length) {
        return "";
      }
      const lossReasonMap = new Map(lossReasons.map((user) => [user.id, user]));
      return (
        <div className={styles.inlineValue}>
          {lossReasonIds
            .map((lossReasonId) => {
              const lossReason = lossReasonMap.get(lossReasonId);
              return lossReason ? <Tag key={lossReason.id}>{lossReason.name}</Tag> : null;
            })
            .filter(isDefined)}
        </div>
      );
    },
    [lossReasons]
  );

  return (
    <SelectComponent
      className={className}
      filterOption={defaultFilterOption}
      label={label}
      loading={loading}
      maxTagCount="responsive"
      mode="multiple"
      onChange={onChange}
      options={selectOptions}
      value={value}
      {...(inline ? { showFooterButtons: false, valueRender } : {})}
      {...props}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  loading: isDealLossReasonsLoading(state),
  lossReasons: getDealLossReasons(state),
});

const mapDispatchToProps = {
  fetchDealLossReasons: fetchDealLossReasons.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(LossReasonSelect);
